import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import {
    deliveryMethodService,
    DeliveryMethodDto,
    BulkUpdateDeliveryMethodRequest,
    LayoutLookupDto
} from '../../../../../services/deliveryMethodService';

interface SelectedRows {
    [key: string]: boolean;
}

interface DeliveryMethodSidePanelProps {
    selectedRows: SelectedRows;
    data: DeliveryMethodDto[];
    onClose: () => void;
    onSave: () => void;
}

interface BulkEditConfig {
    [key: string]: {
        enabled: boolean;
        value: any;
    };
}

type FieldName = 'deliveryMethodNo' | 'description' | 'externalID' | 'consignmentSWDefProductNo' | 'consignmentSWAdditionalCode' | 'shippingLabelsLayoutNo' | 'labelExportFolder' | 'shippingLabelsYesNo' | 'singleParcelDelivery' | 'packageSlipERPYesNo' | 'shippingLabelsERPYesNo' | 'consignmentNoteERPYesNo';

const fields: FieldName[] = [
    'deliveryMethodNo', 'description', 'externalID',
    'consignmentSWDefProductNo', 'consignmentSWAdditionalCode',
    'shippingLabelsLayoutNo', 'labelExportFolder',
    'shippingLabelsYesNo', 'singleParcelDelivery',
    'packageSlipERPYesNo', 'shippingLabelsERPYesNo', 'consignmentNoteERPYesNo'
];

const booleanFields: FieldName[] = [
    'shippingLabelsYesNo', 'singleParcelDelivery',
    'packageSlipERPYesNo', 'shippingLabelsERPYesNo', 'consignmentNoteERPYesNo'
];

const selectFields: FieldName[] = ['shippingLabelsLayoutNo'];

const fieldLabels: Record<FieldName, string> = {
    deliveryMethodNo: 'Leveringsmåte nr.',
    description: 'Beskrivelse',
    externalID: 'Transportør ID',
    consignmentSWDefProductNo: 'TA Produktnr.',
    consignmentSWAdditionalCode: 'TA Tilleggskode',
    shippingLabelsLayoutNo: 'Etikettlayout',
    labelExportFolder: 'Eksportkatalog',
    shippingLabelsYesNo: 'Fraktetiketter',
    singleParcelDelivery: 'Enkeltkolli',
    packageSlipERPYesNo: 'Pakkseddel ERP',
    shippingLabelsERPYesNo: 'Etikett ERP',
    consignmentNoteERPYesNo: 'Fraktbrev ERP'
};

export const DeliveryMethodSidePanel: React.FC<DeliveryMethodSidePanelProps> = ({
    selectedRows,
    data,
    onClose,
    onSave
}) => {
    const [bulkEditConfig, setBulkEditConfig] = useState<BulkEditConfig>({});
    const [layouts, setLayouts] = useState<LayoutLookupDto[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState<string>('');
    const [activeTab, setActiveTab] = useState<'edit' | 'delete'>('edit');

    useEffect(() => {
        deliveryMethodService.getLayouts().then(setLayouts).catch(() => {});
    }, []);

    const selectedRowCount = Object.values(selectedRows).filter(Boolean).length;
    const selectedData = data.filter(item => selectedRows[item.id.toString()]);
    const isSingleSelection = selectedData.length === 1;

    useEffect(() => {
        const initialConfig: BulkEditConfig = {};
        const singleItem = isSingleSelection ? selectedData[0] : null;

        fields.forEach(field => {
            const isBool = booleanFields.includes(field);
            initialConfig[field] = {
                enabled: isSingleSelection,
                value: singleItem ? singleItem[field] : (isBool ? false : (field === 'deliveryMethodNo' ? 0 : ''))
            };
        });

        setBulkEditConfig(initialConfig);
    }, [selectedRows]);

    const handleFieldToggle = (field: string, enabled: boolean) => {
        setBulkEditConfig(prev => ({
            ...prev,
            [field]: { ...prev[field], enabled }
        }));
    };

    const handleFieldValueChange = (field: string, value: any) => {
        setBulkEditConfig(prev => ({
            ...prev,
            [field]: { ...prev[field], value }
        }));
    };

    const handleBulkUpdate = async () => {
        try {
            setIsSaving(true);
            setError('');

            const enabledFields = Object.entries(bulkEditConfig)
                .filter(([, config]) => config.enabled)
                .map(([field, config]) => ({ field, value: config.value }));

            if (enabledFields.length === 0) {
                setError('Ingen felter valgt for oppdatering');
                return;
            }

            const requests: BulkUpdateDeliveryMethodRequest[] = selectedData.map(item => {
                const request: BulkUpdateDeliveryMethodRequest = {
                    companyNo: item.companyNo,
                    deliveryMethodNo: item.deliveryMethodNo,
                    description: item.description,
                    externalID: item.externalID,
                    consignmentSWDefProductNo: item.consignmentSWDefProductNo,
                    consignmentSWAdditionalCode: item.consignmentSWAdditionalCode,
                    shippingLabelsYesNo: item.shippingLabelsYesNo,
                    shippingLabelsLayoutNo: item.shippingLabelsLayoutNo,
                    labelExportFolder: item.labelExportFolder,
                    labelExportFilename: item.labelExportFilename,
                    packageSlipERPYesNo: item.packageSlipERPYesNo,
                    shippingLabelsERPYesNo: item.shippingLabelsERPYesNo,
                    consignmentNoteERPYesNo: item.consignmentNoteERPYesNo,
                    singleParcelDelivery: item.singleParcelDelivery
                };

                enabledFields.forEach(({ field, value }) => {
                    if (field === 'deliveryMethodNo') {
                        request.deliveryMethodNo = typeof value === 'number' ? value : parseInt(value) || item.deliveryMethodNo;
                    } else if (booleanFields.includes(field as FieldName)) {
                        (request as any)[field] = !!value;
                    } else {
                        (request as any)[field] = value;
                    }
                });

                return request;
            });

            await deliveryMethodService.bulkUpdate(requests);

            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                onSave();
            }, 1200);
        } catch (err: any) {
            setError(err.message || 'Feil ved oppdatering');
        } finally {
            setIsSaving(false);
        }
    };

    const handleBulkDelete = async () => {
        try {
            setIsDeleting(true);
            setError('');

            const ids = selectedData.map(item => item.id);
            const result = await deliveryMethodService.bulkDelete(ids);

            if (result.notFoundIds && result.notFoundIds.length > 0) {
                setError(`${result.deletedCount} av ${result.requestedCount} rader ble slettet. Noen rader ble ikke funnet.`);
            } else {
                setShowSuccess(true);
                setTimeout(() => {
                    setShowSuccess(false);
                    onSave();
                }, 1200);
            }
        } catch (err: any) {
            setError(err.message || 'Feil ved sletting');
        } finally {
            setIsDeleting(false);
        }
    };

    const enabledFieldCount = Object.values(bulkEditConfig).filter(config => config.enabled).length;
    const isDeliveryMethodNoEditable = isSingleSelection;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-25 transition-opacity" onClick={onClose} />

            <div className="absolute right-0 top-0 h-full w-[420px] bg-white shadow-xl border-l border-gray-200 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600">
                    <div>
                        <h2 className="text-lg font-semibold text-white">
                            {isSingleSelection ? 'Rediger leveringsmåte' : 'Masseoppdatering'}
                        </h2>
                        <p className="text-sm text-blue-100">
                            {selectedRowCount} {selectedRowCount === 1 ? 'rad' : 'rader'} valgt
                        </p>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Tab Navigation */}
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('edit')}
                        className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                            activeTab === 'edit'
                                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        Rediger
                    </button>
                    <button
                        onClick={() => setActiveTab('delete')}
                        className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                            activeTab === 'delete'
                                ? 'text-red-600 border-b-2 border-red-600 bg-red-50'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                    >
                        Slett
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-red-800">{error}</p>
                            </div>
                        </div>
                    )}

                    {showSuccess && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <p className="text-sm text-green-800 font-medium">
                                    {activeTab === 'edit' ? 'Endringer lagret!' : 'Rader slettet!'}
                                </p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'edit' ? (
                        <>
                            {isSingleSelection && selectedData[0] && (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                                        Nåværende verdier
                                    </h4>
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Lev.måte nr.:</span>
                                            <span className="font-medium text-gray-900">{selectedData[0].deliveryMethodNo}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Beskrivelse:</span>
                                            <span className="font-medium text-gray-900">{selectedData[0].description || '-'}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Transportør ID:</span>
                                            <span className="font-medium text-gray-900">{selectedData[0].externalID || '-'}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!isSingleSelection && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-blue-800">
                                            <p className="font-medium mb-1">Masseoppdatering</p>
                                            <p>Velg feltene du ønsker å oppdatere for alle valgte rader. Kun markerte felt vil bli endret.</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-3">
                                <h3 className="text-sm font-medium text-gray-900">
                                    {isSingleSelection ? 'Rediger felt' : 'Felter som skal oppdateres'}
                                </h3>

                                {fields.map(field => {
                                    const config = bulkEditConfig[field];
                                    if (!config) return null;

                                    const isKeyField = field === 'deliveryMethodNo';
                                    const isEditable = isKeyField ? isDeliveryMethodNoEditable : true;
                                    if (!isEditable) return null;

                                    const isBool = booleanFields.includes(field);

                                    return (
                                        <div
                                            key={field}
                                            className={`border rounded-lg p-3 transition-colors ${
                                                config.enabled ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={config.enabled}
                                                        onChange={(e) => handleFieldToggle(field, e.target.checked)}
                                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                    <span className="text-sm font-medium text-gray-700">
                                                        {fieldLabels[field]}
                                                    </span>
                                                </label>
                                                {isKeyField && (
                                                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                                                        Nøkkelfelt
                                                    </span>
                                                )}
                                            </div>

                                            {config.enabled && (
                                                <div className="mt-2">
                                                    {isBool ? (
                                                        <label className="flex items-center gap-2 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!config.value}
                                                                onChange={(e) => handleFieldValueChange(field, e.target.checked)}
                                                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                                            />
                                                            <span className="text-sm text-gray-600">
                                                                {config.value ? 'Ja' : 'Nei'}
                                                            </span>
                                                        </label>
                                                    ) : selectFields.includes(field) && layouts.length > 0 ? (
                                                        <select
                                                            value={config.value ?? ''}
                                                            onChange={(e) => handleFieldValueChange(field, e.target.value ? parseInt(e.target.value) : null)}
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                                        >
                                                            <option value="">-- Ingen --</option>
                                                            {layouts.map(l => (
                                                                <option key={l.layoutNo} value={l.layoutNo}>
                                                                    {l.layoutNo} - {l.description || ''}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : field === 'deliveryMethodNo' || selectFields.includes(field) ? (
                                                        <input
                                                            type="number"
                                                            value={config.value}
                                                            onChange={(e) => handleFieldValueChange(field, parseInt(e.target.value) || 0)}
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            min={1}
                                                        />
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            value={config.value || ''}
                                                            onChange={(e) => handleFieldValueChange(field, e.target.value)}
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            placeholder={`Ny verdi for ${fieldLabels[field]}`}
                                                            maxLength={100}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {enabledFieldCount > 0 && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                    <div className="flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-green-800">
                                            <p className="font-medium">Klar til å lagre</p>
                                            <p>
                                                {enabledFieldCount} felt vil bli oppdatert for {selectedRowCount} {selectedRowCount === 1 ? 'rad' : 'rader'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-red-800">
                                        <p className="font-medium mb-1">Massesletting</p>
                                        <p>Du er i ferd med å slette {selectedRowCount} {selectedRowCount === 1 ? 'rad' : 'rader'}. Denne handlingen kan ikke angres.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <h3 className="text-sm font-medium text-gray-900">Rader som vil bli slettet:</h3>
                                <div className="max-h-60 overflow-y-auto space-y-1">
                                    {selectedData.map(item => (
                                        <div key={item.id} className="text-xs bg-gray-50 p-2 rounded border border-gray-200">
                                            <span className="font-medium">Lev.måte {item.deliveryMethodNo}:</span> {item.description || '-'}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="flex items-center justify-between gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Avbryt
                        </button>

                        {activeTab === 'edit' ? (
                            <button
                                onClick={handleBulkUpdate}
                                disabled={enabledFieldCount === 0 || isSaving}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    enabledFieldCount === 0 || isSaving
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Lagrer...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        Lagre endringer
                                    </>
                                )}
                            </button>
                        ) : (
                            <button
                                onClick={handleBulkDelete}
                                disabled={isDeleting}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    isDeleting
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        : 'bg-red-600 text-white hover:bg-red-700'
                                }`}
                            >
                                {isDeleting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Sletter...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" />
                                        Slett {selectedRowCount} {selectedRowCount === 1 ? 'rad' : 'rader'}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
